import { WorkshopSection } from './WorkshopSection'
import { workshopCopy } from './workshopCopy'
import { capabilityStatus, type CapabilityStatus } from './capabilityStatus'
import { capabilityIcon } from './capabilityIcon'
import { useWorkshopMode } from './WorkshopModeContext'
import { useComposerStore } from '@/stores/composerStore'
import { useBundleFindings } from '@/hooks/useBundleFindings'
import { useFeature } from '@/hooks/useFeature'
import { useSkillCatalog } from '@/hooks/useSkillCatalog'
import { useWorkflows } from '@/hooks/useWorkflows'
import type { FindingLocation } from '@/utils/bundleFindings'
import type { ApiPersona } from '@/types/persona'
import { SkillsCard } from '@/pages/PersonaWorkshop/SkillsCard'
import { ToolsCard } from '@/pages/PersonaWorkshop/ToolsCard'
import { WorkflowsCard } from '@/pages/PersonaWorkshop/WorkflowsCard'
import { usePersonaAssetGrants } from '@/hooks/usePersonaAssetGrants'
import { SendableAssetsCard } from '@/pages/PersonaWorkshop/SendableAssetsCard'

const STATUS_LABEL: Record<CapabilityStatus, string> = {
  'working': 'working',
  'needs-attention': 'needs attention',
  'outdated': 'outdated',
  'answers-web-chat': 'answers web chat',
}
const STATUS_CLASS: Record<CapabilityStatus, string> = {
  'working': 'text-success',
  'needs-attention': 'text-destructive',
  'outdated': 'text-warning-strong',
  'answers-web-chat': 'text-warning-strong',
}

interface Props { persona: ApiPersona; onJump: (loc: FindingLocation) => void }

export function CapabilitiesSection({ persona, onJump }: Props) {
  const copy = workshopCopy(persona.name)
  const { mode } = useWorkshopMode()
  const findings = useBundleFindings()
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const bundle = draft ?? server
  const skillCatalog = useSkillCatalog().data ?? []
  // Workflow rows only exist when the deployment runs the workflow
  // engine (the workflows router is gated on it). ToolsCard and
  // WorkflowsCard in the advanced branch below self-gate the same way.
  const workflowEngineOn = useFeature('workflow_engine')
  // WorkflowSummary uses `id` (not `workflow_id`) as the catalog key.
  const workflows = useWorkflows({ enabled: workflowEngineOn }).data ?? []
  const toolFrameworkOn = useFeature('tool_framework')
  // SA-47: grants live outside the bundle, so the status row reads the
  // shared grants hook rather than bundleFindings.
  const grants = usePersonaAssetGrants(persona.id)

  const skillRows = (bundle?.default_skills ?? []).map((s) => {
    const cat = skillCatalog.find((c) => c.name === s.name)
    return {
      key: `skill:${s.name}`,
      iconKey: s.name,
      label: cat?.display_name ?? s.name,
      description: cat?.description ?? undefined,
      status: capabilityStatus(findings, 'skills', s.name),
    }
  })
  const workflowRows = (workflowEngineOn ? bundle?.workflow_bindings ?? [] : []).map((b) => {
    const id = (b as { workflow_id: string }).workflow_id
    const wf = workflows.find((w) => w.id === id)
    return {
      key: `wf:${id}`,
      iconKey: id,
      label: wf?.name ?? id,
      description: wf?.description ?? undefined,
      status: capabilityStatus(findings, 'workflows', id),
    }
  })
  const assetRows =
    toolFrameworkOn && !grants.isLoading && grants.activeAssets.length > 0
      ? [{
          key: 'assets:sendable',
          iconKey: 'assets',
          label: 'Share approved assets',
          description:
            grants.sendableCount > 0
              ? `${grants.sendableCount} asset${grants.sendableCount === 1 ? '' : 's'} granted`
              : 'No assets granted yet',
          status: (grants.sendableCount === 0
            ? 'needs-attention'
            : 'working') as CapabilityStatus,
        }]
      : []
  const rows = [...workflowRows, ...skillRows, ...assetRows]
  const attention = rows.filter((r) => r.status !== 'working').length

  return (
    <WorkshopSection id="workshop-capabilities" emoji="🧰" title={copy.canDoTitle} subtitle="Skills, tools & workflows." defaultOpen={false}>
      {mode === 'simple' ? (
        <div>
          <p className="mb-3 text-xs text-muted-foreground">
            {persona.name} can handle <b>{rows.length}</b> thing{rows.length === 1 ? '' : 's'} for your customers
            {rows.length > 0 && (attention === 0 ? ' · all working' : ` · ${attention} need attention`)}
          </p>
          <div className="overflow-hidden rounded-xl border border-border">
            {rows.length === 0 && <p className="p-3 text-sm text-muted-foreground">Nothing installed yet.</p>}
            {rows.map((r) => (
              <div key={r.key} className="flex items-center gap-3 border-b border-border p-3 last:border-b-0">
                <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">{capabilityIcon(r.iconKey)}</span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-foreground">{r.label}</span>
                  {r.description && <span className="block text-xs text-muted-foreground">{r.description}</span>}
                </span>
                <span className={`text-xs ${STATUS_CLASS[r.status]}`}>· {STATUS_LABEL[r.status]}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <SkillsCard onJump={onJump} />
          <ToolsCard />
          <WorkflowsCard onJump={onJump} />
          <SendableAssetsCard personaId={persona.id} personaName={persona.name} />
        </div>
      )}
    </WorkshopSection>
  )
}
